import { US_STATES } from '../constants/states';
import type { ImportMatchCheck } from '../models/import/import-run.models';

/**
 * The duplicate checks a lead import can run (PD-17034), and the company-name
 * rule the run setup has to state plainly. Shared so the wizard's choices and
 * the server's tiers cannot name a check differently.
 */

export interface ImportMatchCheckOptionModel {
	value: ImportMatchCheck;
	label: string;
}

/** Display order: the authoritative identifiers first, company name last. */
export const IMPORT_MATCH_CHECK_OPTIONS: ReadonlyArray<ImportMatchCheckOptionModel> =
	[
		{ value: 'id', label: 'Workd ID' },
		{ value: 'externalId', label: 'External ID' },
		{ value: 'npi', label: 'NPI Number' },
		{ value: 'ncpdp', label: 'NCPDP ID' },
		{ value: 'license', label: 'License Number' },
		{ value: 'email', label: 'Email' },
		{ value: 'phone', label: 'Phone' },
		{ value: 'name', label: 'Company Name' },
	];

export const IMPORT_MATCH_CHECKS: ReadonlyArray<ImportMatchCheck> =
	IMPORT_MATCH_CHECK_OPTIONS.map((option) => option.value);

/**
 * Shown where company name is chosen. The rule is the one the server applies
 * in `companyNameMatchKey` and `importNameMatchLocationsAgree`; keep the two
 * saying the same thing.
 */
export const IMPORT_COMPANY_NAME_MATCH_RULE =
	'A company name matches only when the names are the same, ignoring capital letters, spaces, punctuation and a trailing Inc, LLC or Co, so Wal-Mart, Wal Mart and Walmart Inc are the same name. A name that is only part of the other does not match. It does not match when both records have a state and the states are different, or both have a ZIP code and the ZIP codes are different. When either record has no state or no ZIP code, the name still matches.';

/**
 * The checks a run uses. A run or profile saved before the choice existed
 * carries none, and it must keep behaving as it did: every check. An empty
 * list is a real choice (no checks at all) and is kept as one.
 */
export function resolveImportMatchChecks(
	checks: ReadonlyArray<ImportMatchCheck> | null | undefined,
): ReadonlySet<ImportMatchCheck> {
	if (checks == null) {
		return new Set(IMPORT_MATCH_CHECKS);
	}
	return new Set(checks.filter((check) => IMPORT_MATCH_CHECKS.includes(check)));
}

/** Where one side of a company-name match is, as far as the rule cares. */
export interface ImportNameMatchLocationModel {
	state?: string | null;
	zip?: string | null;
}

/** A state as a two-letter code; a full US state name is read as its code. */
function normalizeMatchState(value: string | null | undefined): string {
	const trimmed = (value ?? '').trim();
	if (trimmed === '') {
		return '';
	}
	if (trimmed.length > 2) {
		const byName = US_STATES.find(
			(state) => state.label.toLowerCase() === trimmed.toLowerCase(),
		);
		if (byName != null) {
			return byName.value;
		}
	}
	return trimmed.toUpperCase();
}

/**
 * A US ZIP as its first five digits (ZIP+4 and a ZIP that lost its leading
 * zero in a spreadsheet compare as the same place). Anything else, such as a
 * Canadian postal code, is compared as written without spaces.
 */
function normalizeMatchZip(value: string | null | undefined): string {
	const trimmed = (value ?? '').trim();
	if (trimmed === '') {
		return '';
	}
	const usZip = /^(\d{1,5})(?:-?\d{4})?$/.exec(trimmed);
	if (usZip?.[1] != null) {
		return usZip[1].padStart(5, '0');
	}
	return trimmed.replace(/\s+/g, '').toUpperCase();
}

/**
 * Whether a company-name match between a row and an existing record stands,
 * given where each one is. Two sites of one business share a name, so a name
 * alone is not enough when the places plainly differ: both sides have a state
 * and the states differ, or both have a ZIP and the ZIPs differ. When either
 * side is missing the state or the ZIP, that part cannot tell them apart and
 * the name still matches.
 */
export function importNameMatchLocationsAgree(
	row: ImportNameMatchLocationModel,
	existing: ImportNameMatchLocationModel,
): boolean {
	const rowState = normalizeMatchState(row.state);
	const existingState = normalizeMatchState(existing.state);
	if (rowState !== '' && existingState !== '' && rowState !== existingState) {
		return false;
	}
	const rowZip = normalizeMatchZip(row.zip);
	const existingZip = normalizeMatchZip(existing.zip);
	if (rowZip !== '' && existingZip !== '' && rowZip !== existingZip) {
		return false;
	}
	return true;
}
